import { DraftsAndSentItem, type DraftsAndSentItemProps } from "@tellescope/ui";
import { useTellescopeMutation, useTellescopeQueries, type TellescopeModelFromName } from "@tellescope/tanstack-query";
import { InboxScheduledItem, useSendScheduledMessage } from "../../../hooks";
import { useTranslation } from "react-i18next";
import { useInboxMessageDeleteMutations, useThreadNavigation } from "../../../../../hooks";
import { Clock, Pencil, Send, Trash2 } from "lucide-react";
import { ScheduleMessageDialog } from "../../conversation-panel";
import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { formatDateTime } from "@tellescope/utils";
import { buildChatCompositionEditLink } from "../../../../../utils";
import { toast } from "sonner";

export interface InboxThreadListScheduledItemProps
  extends Pick<
    DraftsAndSentItemProps<TellescopeModelFromName<"endusers">>,
    "title" | "timestamp" | "preview" | "channel" | "showActions"
  > {
  message: InboxScheduledItem;
  enduserIds: string[];
  chatroomsMap: Record<string, TellescopeModelFromName<"chat_rooms">>;
  className?: string;
}

export const InboxThreadListScheduledItem = ({
  message,
  enduserIds,
  chatroomsMap,
  timestamp,
  ...rest
}: InboxThreadListScheduledItemProps) => {
  const { t } = useTranslation("inbox");
  const navigate = useNavigate();
  const { openThread, openChatThread } = useThreadNavigation();
  const { deleteMessage } = useInboxMessageDeleteMutations();
  const { sendScheduledMessage, isLoading: isSending } = useSendScheduledMessage();

  const showSuccessMessage = useCallback(
    (sendAt: Date) => {
      toast.success(
        t("Successfully rescheduled for {{date}}", {
          date: formatDateTime(sendAt, "MM-DD-YYYY at hh:mm a", {
            unit: "weeks",
            amount: 1,
            direction: "both",
          }),
        })
      );
    },
    [t]
  );

  const scheduleSmsMutation = useTellescopeMutation({
    model: "sms_messages",
    method: "updateOne",

    onSuccess: (req) => {
      if (!req.sendAt) return;
      showSuccessMessage(req.sendAt);
    },
  });
  const scheduleChatMutation = useTellescopeMutation({
    model: "chats",
    method: "updateOne",

    onSuccess: (req) => {
      if (!req.sendAt) return;
      showSuccessMessage(req.sendAt);
    },
  });
  const scheduleEmailMutation = useTellescopeMutation({
    model: "emails",
    method: "updateOne",

    onSuccess: (req) => {
      if (!req.sendAt) return;
      showSuccessMessage(req.sendAt);
    },
  });

  const [scheduleDialogIsOpen, setScheduleDialogIsOpen] = useState(false);

  const chatroom = message.channel === "Chat" ? chatroomsMap[message.roomId] : undefined;

  const endusersQuery = useTellescopeQueries({
    model: "endusers",
    method: "getOne",
    requests: enduserIds.length ? enduserIds : chatroom?.enduserIds ?? [],
    combine: (results) =>
      results.reduce<TellescopeModelFromName<"endusers">[]>((acc, curr) => {
        if (curr.data) {
          acc.push(curr.data);
        }
        return acc;
      }, []),
  });

  return (
    <>
      <DraftsAndSentItem
        key={message.id}
        endusers={endusersQuery}
        {...rest}
        timestamp={timestamp}
        actions={[
          {
            key: "edit",
            icon: Pencil,
            hoverLabel: t("Edit Message"),
            onClick: async () => {
              if (message.channel === "Chat") {
                const navigated = await openChatThread(message.roomId, message.id);
                if (!navigated) {
                  navigate(
                    buildChatCompositionEditLink({
                      roomId: message.roomId,
                      draftId: message.id,
                      enduserId: message.enduserId ?? chatroom?.enduserIds?.[0] ?? undefined,
                      subject: chatroom?.title,
                    })
                  );
                }
                return;
              }
              openThread({ type: message.channel, message, draftId: message.id });
            },
          },
          {
            key: "schedule",
            icon: Clock,
            hoverLabel: t("Reschedule Message"),
            onClick: () => {
              setScheduleDialogIsOpen(true);
            },
          },
          {
            key: "send-now",
            icon: Send,
            hoverLabel: t("Send Message Now"),
            onClick: async () => {
              try {
                await sendScheduledMessage(message);
              } catch (error) {
                console.error("Failed to send scheduled message:", error);
                toast.error(t("Failed to send message"));
              }
            },
            disabled: isSending,
          },
          {
            key: "delete",
            icon: Trash2,
            hoverLabel: t("Delete Message"),
            onClick: () => {
              deleteMessage(message.id, message.channel);
            },
          },
        ]}
      />
      <ScheduleMessageDialog
        open={scheduleDialogIsOpen}
        onOpenChange={setScheduleDialogIsOpen}
        initDate={message.sendAt || undefined}
        onSchedule={(sendAt) => {
          switch (message.channel) {
            case "SMS":
              return scheduleSmsMutation.mutateAsync({
                id: message.id,
                sendAt,
              });
            case "Chat":
              return scheduleChatMutation.mutateAsync({
                id: message.id,
                sendAt,
              });
            case "Email":
              return scheduleEmailMutation.mutateAsync({
                id: message.id,
                sendAt,
              });
          }
        }}
      />
    </>
  );
};
